// Orders sub-tab for In-Room QR admin panel.
// All JSX built inside the main function via hoisted `.map()` calls — the
// visual-edits Babel plugin stack-overflows on sub-components that `.map()`
// their own props, so we keep everything in one component.
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, ChevronRight, Check, X, Coffee, Receipt } from 'lucide-react';
import { apiCall } from './_shared';

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;

const InRoomOrdersTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('new');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const json = await apiCall(`/api/admin/inroom/orders${qs}`);
      setData(json);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const advance = async (id, s) => {
    try {
      await apiCall(`/api/admin/inroom/orders/${id}`, {
        method: 'PATCH', body: JSON.stringify({ status: s }),
      });
      load();
    } catch (e) { alert(e.message); }
  };

  const attachToFolio = async (id) => {
    try {
      const r = await apiCall(`/api/admin/inroom/orders/${id}/attach-folio`, { method: 'POST' });
      alert(`Charged to folio · new balance €${r.folio_balance?.toFixed(2)}`);
      load();
    } catch (e) { alert(e.message); }
  };

  const items = data?.items || [];
  const c = data?.counts || {};

  const pill = (s) => {
    if (s === 'new') return 'bg-orange-100 text-orange-700';
    if (s === 'prepared') return 'bg-blue-100 text-blue-700';
    if (s === 'delivered') return 'bg-green-100 text-green-700';
    return 'bg-slate-200 text-slate-500';
  };

  const filterBtns = [
    { k: 'new', label: `New (${c.new || 0})` },
    { k: 'prepared', label: `Prepared (${c.prepared || 0})` },
    { k: 'delivered', label: `Delivered (${c.delivered || 0})` },
    { k: '', label: 'All' },
  ].map((f) => (
    <button
      key={f.k}
      onClick={() => setStatusFilter(f.k)}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${statusFilter === f.k ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
      data-testid={`orders-filter-${f.k || 'all'}`}
    >
      {f.label}
    </button>
  ));

  // Build order cards inline (no sub-component — visual-edits plugin chokes on props.map).
  const orderCards = items.map((o, oi) => {
    const lineRows = (o.items || []).map((it, i) => (
      <li key={i} className="flex justify-between text-slate-700">
        <span>{it.qty}× {it.name}</span>
        <span className="text-slate-500">{eur(it.line_total)}</span>
      </li>
    ));
    const canAdvance = o.status !== 'delivered' && o.status !== 'cancelled';
    return (
      <div key={o.id || oi} className="bg-white rounded-xl border p-4" data-testid={`order-card-${o.id}`}>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
              Room {o.room_id}{o.guest_name ? ` · ${o.guest_name}` : ''}
            </p>
            <p className="text-base font-bold text-slate-900 mt-0.5">
              {(o.items || []).length} item(s) · <span className="text-orange-600">{eur(o.total_eur)}</span>
            </p>
            {o.room_name && (
              <p className="text-xs text-slate-400">
                {o.unit_name && <span className="font-bold text-slate-600">Room {o.unit_name} · </span>}
                {o.room_name}
              </p>
            )}
          </div>
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${pill(o.status)}`}>{o.status}</span>
        </div>
        <ul className="mt-3 text-sm border-t pt-2 space-y-1">{lineRows}</ul>
        {o.note && <p className="mt-2 text-xs bg-amber-50 text-amber-900 rounded px-2 py-1.5 border border-amber-200">Note: {o.note}</p>}
        {o.linked_folio_booking_id ? (
          <p className="mt-2 text-xs bg-emerald-50 text-emerald-800 rounded px-2 py-1.5 border border-emerald-200 inline-flex items-center gap-1" data-testid={`order-folio-${o.id}`}>
            <Receipt className="w-3.5 h-3.5" /> Charged to folio {o.linked_folio_booking_id}
          </p>
        ) : (
          <button
            onClick={() => attachToFolio(o.id)}
            className="mt-2 text-xs bg-white hover:bg-emerald-50 border border-emerald-300 text-emerald-700 rounded px-2 py-1.5 inline-flex items-center gap-1 font-semibold"
            data-testid={`order-attach-${o.id}`}
          >
            <Receipt className="w-3.5 h-3.5" /> Add to folio
          </button>
        )}
        {canAdvance && (
          <div className="mt-3 flex gap-2 flex-wrap">
            {o.status === 'new' && (
              <button onClick={() => advance(o.id, 'prepared')} className="px-3 py-1.5 rounded-full bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold flex items-center gap-1" data-testid={`order-prepare-${o.id}`}>
                <ChevronRight className="w-3.5 h-3.5" /> Mark prepared
              </button>
            )}
            <button onClick={() => advance(o.id, 'delivered')} className="px-3 py-1.5 rounded-full bg-green-500 hover:bg-green-600 text-white text-xs font-semibold flex items-center gap-1" data-testid={`order-deliver-${o.id}`}>
              <Check className="w-3.5 h-3.5" /> Delivered
            </button>
            <button onClick={() => advance(o.id, 'cancelled')} className="px-3 py-1.5 rounded-full bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-xs font-semibold flex items-center gap-1" data-testid={`order-cancel-${o.id}`}>
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        )}
      </div>
    );
  });

  return (
    <div data-testid="inroom-orders-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">{filterBtns}</div>
        <button onClick={load} disabled={loading} className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm" data-testid="orders-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </button>
      </div>
      {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
      {items.length === 0 && !loading ? (
        <div className="text-center py-16 text-slate-400 bg-white rounded-xl border" data-testid="orders-empty">
          <Coffee className="w-10 h-10 mx-auto mb-2" />
          <p className="text-sm">No {statusFilter || ''} orders right now.</p>
        </div>
      ) : (
        <div className="space-y-3">{orderCards}</div>
      )}
    </div>
  );
};

export default InRoomOrdersTab;
